import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { getAuthToken } from '../utils/auth'
import secureStorage from '../utils/secureStorage'

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://pro.vibelinksapp.com/api/'

// Async thunk to fetch notifications
export const fetchNotifications = createAsyncThunk(
  'notification/fetchNotifications',
  async (page = 1, { rejectWithValue }) => {
    try {
      const token = secureStorage.getItem('accessToken')
      const formData = new FormData()

      const { getCurrentFCMToken } = await import('../utils/fcmToken');
      const fcmToken = await getCurrentFCMToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        'Accept-Language': 'en',
      };
      
      if (fcmToken) {
        headers['FCM-Token'] = fcmToken;
      }
      
      const response = await axios.post(
        `${BASE_URL}/get-notification${page > 1 ? `?page=${page}` : ''}`,
        formData,
        { headers }
      )

      if (!response.data.success) {
        return rejectWithValue(response.data.message)
      }

      const notifications = response.data.data.data
      const unreadCount = notifications.filter(n => !n.read).length

      return {
        notifications,
        totalPage: response.data.data.last_page,
        nextPage:
          response.data.data.current_page < response.data.data.last_page
            ? response.data.data.current_page + 1
            : null,
        unreadCount
      }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message)
    }
  }
)

// Async thunk to mark notification as read
export const markNotificationAsRead = createAsyncThunk(
  'notification/markAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const token = getAuthToken()
      const response = await axios.post(
        `${BASE_URL}/read-notification`,
        { notification_id: notificationId },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (!response.data.success) {
        return rejectWithValue(response.data.message)
      }

      return { notificationId, success: true }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message)
    }
  }
)

// Async thunk to delete notification
export const deleteNotification = createAsyncThunk(
  'notification/deleteNotification',
  async (notificationId, { rejectWithValue }) => {
    try {
      const token = getAuthToken()
      const response = await axios.post(
        `${BASE_URL}/delete-notification`,
        { notification_id: notificationId },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (!response.data.success) {
        return rejectWithValue(response.data.message)
      }

      return { notificationId, success: true }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message)
    }
  }
)

const initialState = {
  notifications: [],
  loading: false,
  error: null,
  hasMore: true,
  nextPage: 1,
  totalPage: 1,
  unreadCount: 0
}

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    clearNotifications: state => {
      state.notifications = []
      state.loading = false
      state.error = null
      state.hasMore = true
      state.nextPage = 1
      state.totalPage = 1
      state.unreadCount = 0
    },
    resetError: state => {
      state.error = null
    }
  },
  extraReducers: builder => {
    // Fetch notifications
    builder
      .addCase(fetchNotifications.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const { notifications, totalPage, nextPage, unreadCount } =
          action.payload
        const mappedNotifications = notifications

        if (action.meta.arg === 1) {
          state.notifications = mappedNotifications
          state.unreadCount = unreadCount
        } else {
          const newNotifications = mappedNotifications.filter(
            newNotif =>
              !state.notifications.some(existing => existing.id === newNotif.id)
          )
          state.notifications = [...state.notifications, ...newNotifications]
        }

        state.loading = false
        state.totalPage = totalPage
        state.hasMore = nextPage !== null
        state.nextPage = action.meta.arg + 1
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Mark as read
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const { notificationId } = action.payload
        const notification = state.notifications.find(
          n => n.id === notificationId
        )
        if (notification) {
          notification.read = true
          state.unreadCount = Math.max(0, state.unreadCount - 1)
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.error = action.payload
      })

      // Delete notification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const { notificationId } = action.payload
        const deletedNotification = state.notifications.find(
          n => n.id === notificationId
        )

        state.notifications = state.notifications.filter(
          n => n.id !== notificationId
        )

        if (deletedNotification && !deletedNotification.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1)
        }
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.error = action.payload
      })
  }
})

export const { clearNotifications, resetError } = notificationSlice.actions
export default notificationSlice.reducer

// Selectors
export const selectUnreadCount = state => state.notifications?.unreadCount || 0

export const selectNotificationsByType = (state, type) =>
  state.notifications?.notifications?.filter(n => n.type === type) || []

export const selectUnreadNotifications = state =>
  state.notifications?.notifications?.filter(n => !n.read) || []

export const selectLatestNotifications = (state, count = 5) =>
  state.notifications?.notifications?.slice(0, count)?.map(n => ({
    id: n.id,
    user: n.sender,
    userImage: n.sender.image,
    message: n.body,
    time: n.created_at,
    type: n.type,
    read: n.read
  })) || []
